import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography } from '@crisellevaldez/speculo-ui';
import { useToast } from '@crisellevaldez/speculo-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Contact } from '../../../../types/contact.types';
import { useContactsByEmail } from '../../../../hooks/useContactsByEmail';
import { ContactFormData } from '../../../../schemas/contact.schema';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { determineParameterTypes, prepareContactParams } from '../../../../utils/contactParams';
import { ContactEditContent } from '../ContactEditContent';
import { useContact } from '../../../../hooks/graphql/contacts/useContact';
import { useLayoutConfig } from '../../../../hooks/useLayoutConfig';
import { giveBackToRemi } from '../../../../api/handoff';
import { ConfirmationDialog } from '../../../common/dialogs/ConfirmationDialog';

/**
 * ContactEditPage component that handles multiple URL patterns:
 * - /contact/:orgId/:email/:phone
 * - /contact/:orgId/:email
 * - /contact/:orgId/:phone
 * - /contact/:contactId
 *
 * It intelligently determines what each parameter represents based on its format.
 */
const ContactEditPage: React.FC = () => {
  // Get all URL parameters
  const params = useParams<Record<string, string | undefined>>();
  const orgIdFromUrl = params.orgId;
  const { topPadding } = useLayoutConfig();

  // Determine parameter types (email, phone, contactId)
  const { email, phone, contactId, hasEmail, hasPhone, hasContactId } =
    determineParameterTypes(params);

  // For email/phone lookups we still need the Firestore hook to resolve to a contactId
  const {
    getContactsByEmail,
    loading: emailLookupLoading,
    error: emailLookupError,
  } = useContactsByEmail();
  const [resolvedContactId, setResolvedContactId] = useState<string | undefined>(
    hasContactId ? contactId : undefined
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [localOverride, setLocalOverride] = useState<Partial<Contact> | null>(null);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();
  const assistantName = organization?.assistant_name || 'Remi';

  // GraphQL: fetch full contact once we have a contactId
  const {
    contact: gqlContact,
    isLoading: gqlLoading,
    error: gqlError,
    refetch,
  } = useContact({
    contactId: resolvedContactId,
  });

  const selectedContact = gqlContact
    ? localOverride
      ? ({ ...gqlContact, ...localOverride } as Contact)
      : gqlContact
    : null;

  // Only show the page-level skeleton on the initial load (no data yet).
  // Refetches (after mutations, focus, etc.) keep `gqlLoading=true` because of
  // notifyOnNetworkStatusChange, but we already have data — don't re-skeleton.
  const loading = emailLookupLoading || (gqlLoading && !gqlContact);
  const error = emailLookupError || gqlError;

  // Mutation for releasing the active handoff back to Remi
  const releaseToRemiMutation = useMutation({
    mutationFn: () => {
      if (!selectedContact?.id || !selectedContact.activeHandoffId || !organization?.id) {
        throw new Error('Missing contact, organization, or handoff');
      }
      // See useContactData for why no agentId is sent.
      return giveBackToRemi({
        organizationId: organization.id,
        contactId: selectedContact.id,
        handoffId: selectedContact.activeHandoffId,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        setShowReleaseConfirm(false);
        showToast({
          message: `Lead released back to ${assistantName}`,
          variant: 'success',
        });
        // Invalidate queries to refresh contact data
        queryClient.invalidateQueries({ queryKey: ['contact', selectedContact?.id] });
        queryClient.invalidateQueries({ queryKey: ['next-follow-up', selectedContact?.id] });
        refetch();
        // Optimistic update via local override
        setLocalOverride({ claimedLead: undefined, hasClaimedLead: false, flywheelOptOut: false });
      } else {
        showToast({
          message: result.error || 'Failed to release lead',
          variant: 'error',
        });
      }
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to release lead',
        variant: 'error',
      });
    },
  });

  // Resolve email/phone to a contactId once on mount (if not already a contactId URL)
  if (!isInitialized && !emailLookupLoading) {
    setIsInitialized(true);

    if (!hasEmail && !hasPhone && !hasContactId) {
      showToast({ message: 'Email, phone, or contact ID is required', variant: 'error' });
      navigate('/contacts');
    } else if (!orgIdFromUrl && !organization?.id) {
      showToast({ message: 'No organization available', variant: 'error' });
      navigate('/contacts');
    } else if (hasContactId) {
      // Already have a contactId — GraphQL hook picks it up via resolvedContactId
    } else {
      // Need to resolve email/phone → contactId via Firestore lookup
      const { emailParam, phoneParam, contactIdParam } = prepareContactParams({
        email,
        phone,
        contactId,
      });
      getContactsByEmail(emailParam, phoneParam, contactIdParam)
        .then((fetchedContacts) => {
          if (fetchedContacts.length > 0) {
            setResolvedContactId(fetchedContacts[0].id);
          } else {
            showToast({
              message: hasEmail
                ? hasPhone
                  ? 'No contacts found with this email or phone number'
                  : 'No contacts found with this email'
                : 'No contacts found with this phone number',
              variant: 'error',
            });
            navigate('/contacts');
          }
        })
        .catch((err) => {
          showToast({
            message: err instanceof Error ? err.message : 'Failed to fetch contacts',
            variant: 'error',
          });
        });
    }
  }

  const handleContactUpdate = async (_data: ContactFormData) => {
    // Toast and refetch are handled inside useContactData / useContactMutations
  };

  // Render loading state
  if (loading) {
    return (
      <div className="3xl:container mx-auto px-4 animate-pulse">
        {/* Header skeleton */}
        <div className="flex flex-col gap-1 mb-6 mt-4">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />
            <div className="h-6 w-48 rounded bg-muted" />
            <div className="h-5 w-14 rounded-full bg-muted" />
            <div className="h-5 w-20 rounded-full bg-muted" />
          </div>
          <div className="h-4 w-44 rounded bg-muted ml-12" />
        </div>
        {/* Tabs skeleton */}
        <div className="flex gap-1 p-1 rounded-lg bg-muted/40 mb-4 w-fit">
          {[120, 80, 72, 96, 88, 100, 108, 96].map((w, i) => (
            <div key={i} className="h-7 rounded-md bg-muted" style={{ width: w }} />
          ))}
        </div>
        {/* Activity rows skeleton */}
        <div className="space-y-3 mt-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-lg border border-foreground/10"
            >
              <div className="w-8 h-8 rounded-full bg-muted flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 rounded bg-muted" />
                <div className="h-3 w-56 rounded bg-muted" />
              </div>
              <div className="h-3 w-16 rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Typography variant="h4" className="text-red-500">
          Error: {error.message}
        </Typography>
      </div>
    );
  }

  // Contact not found (loaded successfully but not in org or doesn't exist)
  if (!selectedContact && resolvedContactId) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3 px-4">
        <Typography variant="h4" className="text-muted-foreground">
          Contact not found
        </Typography>
        <Typography variant="small" className="text-muted-foreground">
          This contact does not exist or does not belong to your organization.
        </Typography>
      </div>
    );
  }

  // If there's only one contact, show the contact edit form directly on the page
  if (selectedContact) {
    return (
      <div
        className="contact-page-bg flex flex-col px-4 md:px-6"
        style={{ height: `calc(100dvh - ${topPadding}px)`, paddingTop: 16 }}
      >
        <div className="mx-auto max-w-[1700px] 3xl:max-w-[2000px] w-full flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 flex flex-col pb-4">
            <ContactEditContent
              contact={selectedContact}
              onSubmit={handleContactUpdate}
              onSuccess={handleContactUpdate}
              defaultTab="activity"
              showHeader={false}
            />
          </div>

          {/* Release to Remi Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={showReleaseConfirm}
            onClose={() => setShowReleaseConfirm(false)}
            onConfirm={() => releaseToRemiMutation.mutate()}
            title={`Release Lead to ${assistantName}`}
            message={`Are you sure you want to release this lead back to ${assistantName}? The contact will be re-enrolled in the automated follow-up flywheel.`}
            confirmLabel="Yes, Release"
            cancelLabel="Cancel"
            variant="warning"
            isLoading={releaseToRemiMutation.isPending}
          />
        </div>
      </div>
    );
  }
  return null;
};

export default ContactEditPage;
